import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../lib/cookieConsent';

const CookieBanner = () => {
  const { isBannerOpen, isSettingsOpen, acceptAll, rejectAll, openSettings, closeSettings, savePreferences, consent } = useCookieConsent();
  const [prefs, setPrefs] = useState({ preferences: false, analytics: false, thirdParty: false });

  useEffect(() => {
    if (isSettingsOpen) {
      setPrefs({
        preferences: !!consent.preferences,
        analytics: !!consent.analytics,
        thirdParty: !!consent.thirdParty,
      });
    }
  }, [isSettingsOpen, consent]);

  if (!isBannerOpen && !isSettingsOpen) return null;

  if (isSettingsOpen) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/40 px-4 py-6" data-testid="cookie-settings-overlay">
        <div className="w-full max-w-2xl bg-white border border-stone-200 shadow-xl p-8 lg:p-10" data-testid="cookie-settings-panel">
          <div className="overline mb-4">Cookies</div>
          <h2 className="display-md text-cyan-vidat">Configurar cookies</h2>
          <span className="section-rule mt-6 mb-8"></span>
          <p className="text-sm text-stone-600 font-light leading-relaxed mb-8">
            Activa o desactiva cada categoría según tus preferencias. Las cookies necesarias no se pueden desactivar porque son imprescindibles para el funcionamiento de la web.
          </p>

          <div className="space-y-5 mb-10">
            <CategoryRow
              title="Necesarias"
              description="Imprescindibles para el funcionamiento básico de la web."
              checked
              disabled
              testId="cookie-cat-necessary"
            />
            <CategoryRow
              title="Preferencias"
              description="Recuerdan opciones elegidas, como el idioma o ajustes de visualización."
              checked={prefs.preferences}
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
              testId="cookie-cat-preferences"
            />
            <CategoryRow
              title="Analíticas"
              description="Permiten conocer cómo se utiliza la web de forma agregada para mejorarla."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testId="cookie-cat-analytics"
            />
            <CategoryRow
              title="Terceros"
              description="Cookies de servicios externos (mapas, vídeos, herramientas integradas) cuando se utilicen."
              checked={prefs.thirdParty}
              onChange={(v) => setPrefs((p) => ({ ...p, thirdParty: v }))}
              testId="cookie-cat-thirdparty"
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Link to="/politica-de-cookies" className="text-sm text-cyan-vidat hover:underline" data-testid="cookie-settings-policy-link">
              Más información en la Política de Cookies
            </Link>
            <div className="flex flex-wrap gap-3">
              <button onClick={rejectAll} className="btn-secondary" data-testid="cookie-settings-reject">
                Rechazar
              </button>
              <button onClick={acceptAll} className="btn-secondary" data-testid="cookie-settings-accept-all">
                Aceptar todas
              </button>
              <button onClick={() => savePreferences(prefs)} className="btn-primary" data-testid="cookie-settings-save">
                Guardar preferencias
              </button>
              <button onClick={closeSettings} className="text-xs text-stone-400 hover:text-stone-600 ml-2" data-testid="cookie-settings-close">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Primary banner
  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 md:px-6 md:pb-6" data-testid="cookie-banner">
      <div className="max-w-[1200px] mx-auto bg-white border border-stone-200 shadow-xl">
        <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7">
            <div className="overline mb-3">Cookies</div>
            <p className="text-sm text-stone-700 font-light leading-relaxed">
              Utilizamos cookies propias y de terceros para garantizar el funcionamiento de la web, analizar la navegación y mejorar nuestros servicios. Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.
            </p>
            <Link to="/politica-de-cookies" className="text-xs text-cyan-vidat hover:underline inline-block mt-3" data-testid="cookie-banner-policy-link">
              Ver Política de Cookies
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={rejectAll} className="btn-secondary flex-1 justify-center" data-testid="cookie-reject">
                Rechazar cookies
              </button>
              <button onClick={acceptAll} className="btn-primary flex-1 justify-center" data-testid="cookie-accept">
                Aceptar cookies
              </button>
            </div>
            <button onClick={openSettings} className="block w-full text-center mt-3 text-sm text-cyan-vidat hover:underline" data-testid="cookie-configure">
              Configurar cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryRow = ({ title, description, checked, onChange, disabled, testId }) => (
  <div className="flex items-start justify-between gap-6 py-4 border-t border-stone-200" data-testid={testId}>
    <div className="flex-1">
      <div className="font-medium text-cyan-vidat text-sm">{title}</div>
      <div className="text-xs text-stone-500 font-light leading-relaxed mt-1">{description}</div>
    </div>
    <label className={`relative inline-block w-11 h-6 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={!!checked}
        onChange={(e) => !disabled && onChange?.(e.target.checked)}
        disabled={disabled}
      />
      <span className="absolute inset-0 bg-stone-300 peer-checked:bg-[#1E3A8A] rounded-full transition-colors"></span>
      <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
    </label>
  </div>
);

export default CookieBanner;
